import type { CsmHealthDrivers, CsmRosterRow } from '../../../api/dashboard';

/** The computed CS health bands (compute-health: Healthy ≥67 · At risk 40–66 · Critical <40). */
export type HealthBand = 'Healthy' | 'At risk' | 'Critical';

export const HEALTH_BANDS: readonly HealthBand[] = ['Healthy', 'At risk', 'Critical'];

/** The stored tier ("At-risk") as shown ("At risk"); null for anything unknown. */
export function computedBand(tier: string | null | undefined): HealthBand | null {
  const t = (tier ?? '').toLowerCase().replace(/[^a-z]/g, '');
  if (t === 'healthy') return 'Healthy';
  if (t === 'atrisk') return 'At risk';
  if (t === 'critical') return 'Critical';
  return null;
}

/**
 * The CSM's own Attio label on the same three bands: Excellent and Good read
 * as Healthy, Fair and Neutral as At risk, Risk as Critical. Null when unset or
 * unknown, so it never counts as a disagreement.
 */
export function manualBand(label: string | null | undefined): HealthBand | null {
  switch ((label ?? '').trim().toLowerCase()) {
    case 'excellent':
    case 'good':
      return 'Healthy';
    case 'fair':
    case 'neutral':
      return 'At risk';
    case 'risk':
      return 'Critical';
    default:
      return null;
  }
}

/** How many bands apart the two calls are (0 = agree), or null when either is missing. */
export function bandGap(row: Pick<CsmRosterRow, 'health' | 'computedHealth'> & { totalMembers?: number }): number | null {
  if (row.totalMembers === 0) return null;
  const a = computedBand(row.computedHealth?.tier);
  const b = manualBand(row.health);
  if (!a || !b) return null;
  return Math.abs(HEALTH_BANDS.indexOf(a) - HEALTH_BANDS.indexOf(b));
}

/** The roster's Health filter: a computed band, or the rows where Attio disagrees. */
export type HealthFilter = '' | HealthBand | 'disagrees' | 'unscored';

export function matchesHealthFilter(
  row: Pick<CsmRosterRow, 'health' | 'computedHealth'> & { totalMembers?: number },
  filter: HealthFilter,
): boolean {
  if (!filter) return true;
  if (filter === 'disagrees') return (bandGap(row) ?? 0) > 0;
  if (filter === 'unscored') return !row.computedHealth || row.totalMembers === 0;
  if (row.totalMembers === 0) return false;
  return computedBand(row.computedHealth?.tier) === filter;
}

/** The band thresholds, said wherever a score is explained. */
export const BAND_THRESHOLDS = 'Healthy 67+, At risk 40 to 66, Critical under 40';

/**
 * Pure: what a score is made of, in one line, so the reader sees what drags it:
 * "Usage 80 (recency 40/40, active members 10/30 at 32%, used yesterday 30/30)
 * · no CS calls, so usage only · early-warning decline −10 (cap 66)". Null
 * when the API sends no drivers. Exported for tests.
 */
export function healthDriversText(d: CsmHealthDrivers | null | undefined): string | null {
  if (!d) return null;
  const parts: string[] = [];
  const usageDetail =
    d.recency !== null && d.active !== null && d.trend !== null
      ? ` (recency ${d.recency}/40, active members ${d.active}/30${d.activeRate30d !== null ? ` at ${Math.round(d.activeRate30d)}%` : ''}, used yesterday ${d.trend}/30)`
      : '';
  parts.push(`Usage ${d.usage}/100${usageDetail}`);
  parts.push(d.sentiment === null ? 'no CS call mentions it, so usage only' : `call sentiment ${d.sentiment}/100, averaged 50/50 with usage`);
  if (d.idleCap) parts.push('no tokens in 30 days or no active member: capped at 38');
  if (d.declinePenalty > 0) {
    parts.push(
      d.declineSeverity === 'confirmed'
        ? `confirmed decline −${d.declinePenalty} (capped at 55)`
        : `early-warning decline −${d.declinePenalty} (capped at 66)`,
    );
  }
  return parts.join(' · ');
}
